import React, { useEffect, useRef, useState } from 'react';
import { FormikProps } from 'formik';
import { Input } from 'formik-antd';
import { Col, Row, Tooltip } from 'antd';
import { isNil } from 'lodash';
import cx from 'classnames';
import * as Yup from 'yup';

import { ExtraItemEnum } from 'enums';
import { clearFormError } from 'utils/formUtils';
import FormItem from 'components/formik/FormItem';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import {
  AssignmentTypeEnum,
  ContractorCompanyModel,
  ResourceDownloadResponse,
} from 'api/backofficeApiClient';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { getContractorCompanyInfoFile } from 'redux/reducers/contractorCompanySlice';
import { WorkerInfoFormValues as ICWorkerInfoFormValues } from '../ICAssignments/WorkerInfo';
import { WorkerInfoFormValues as SCWorkerInfoFormValues } from '../SCAssignments/WorkerInfo';
import WorkerUploader from 'components/modals/WorkerModal/WorkerUpload';
import { WorkerTypeEnum } from 'api/adminApiClient';
import InputNumber from 'components/formik/InputNumber';
import { BaseUploadFile } from 'components/wrappers/BaseFileUpload';
import { generateUploadFileList } from 'utils/fileUtils';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { countryUSAid } from 'constants/index';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';
import CompanyStateForm from './CompanyStateForm';
import InternationalCompanyStateForm from './InternationalCompanyStateForm';

import styles from '../AssignmentModal/AssignmentModal.module.less';

export type WorkerInfoFormValues =
  | ICWorkerInfoFormValues
  | SCWorkerInfoFormValues;

type Props = {
  companies: ContractorCompanyModel[];
  assignmentType?: AssignmentTypeEnum;
  formik: FormikProps<WorkerInfoFormValues>;
  predefinedInitialValues?: WorkerInfoFormValues;
  contractorCompanyFileInfo?: ResourceDownloadResponse;
  isCompaniesLoaded: boolean;
  isRenewOrEditRenewMode: boolean;
  isRehireOrEditRehireMode: boolean;
  hasActiveAssignmentRestriction: boolean;
};

export interface CompanyFormValues {
  companyId: string;
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country?: number;
  internationalCityName?: string;
  internationalStateName?: string;
  state?: number;
  city: string;
  address: string;
  zip: string;
}

export const companyInitialValidationSchema = {
  companyId: Yup.string(),
  companyName: Yup.string()
    .max(120, 'Company name should be 120 characters maximum')
    .companyDuplicateCheck(),
  firstName: Yup.string()
    .trim()
    .max(120, 'Primary contact first name should be 120 characters maximum'),
  lastName: Yup.string()
    .trim()
    .max(120, 'Primary contact last name should be 120 characters maximum'),
  email: Yup.string()
    .email('Email format is invalid')
    .max(120, 'Email should be 120 characters maximum')
    .companyDuplicateCheck(),
  phone: Yup.string()
    .trim()
    .max(120, 'Phone number should be 120 characters maximum'),
  country: Yup.number().nullable(),
  state: Yup.string().trim(),
  internationalStateName: Yup.string()
    .trim()
    .max(120, 'State should be 120 characters maximum'),
  city: Yup.string().trim().max(120, 'City should be 120 characters maximum'),
  internationalCityName: Yup.string()
    .trim()
    .max(120, 'City should be 120 characters maximum'),
  address: Yup.string()
    .trim()
    .max(120, 'Address should be 120 characters maximum'),
  zip: Yup.string()
    .trim()
    .max(120, 'ZIP code should be 120 characters maximum'),
};

export const companyInitialValues: CompanyFormValues = {
  companyId: '',
  companyName: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  country: undefined,
  state: undefined,
  internationalStateName: '',
  city: '',
  internationalCityName: '',
  address: '',
  zip: '',
};

export const isAddCompanySelected = (option: string) =>
  option === ExtraItemEnum.AddCompany;

const getAddCompanyLabel = (assignmentType?: AssignmentTypeEnum) => {
  return assignmentType === AssignmentTypeEnum.SCAssignment
    ? 'Add new parent SC company'
    : 'Add new contractor company';
};

export default function CompanySection(props: Props) {
  const {
    formik,
    companies,
    predefinedInitialValues,
    assignmentType,
    contractorCompanyFileInfo,
    isCompaniesLoaded,
    isRenewOrEditRenewMode,
    isRehireOrEditRehireMode,
    hasActiveAssignmentRestriction,
  } = props;
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(isAdminSelector);
  const [contractorCompanyFileKeyInfo, setContractorCompanyFileKeyInfo] =
    useState<BaseUploadFile[]>(
      contractorCompanyFileInfo
        ? generateUploadFileList(contractorCompanyFileInfo)
        : [],
    );
  const [companyDetails, setCompanyDetails] =
    useState<ContractorCompanyModel>();
  const [filteredCompanies, setFilteredCompanies] = useState<
    ContractorCompanyModel[]
  >([]);
  const companyFieldRef: any = useRef();
  const isSCAssignment = assignmentType === AssignmentTypeEnum.SCAssignment;
  const isSCWorkerType = formik.values?.worker.workerType === WorkerTypeEnum.SC;
  const isAddCompanyOptionSelected = isAddCompanySelected(
    formik.values.company?.companyId,
  );

  useEffect(() => {
    const predefinedCompanyId = predefinedInitialValues?.company.companyId;

    if (!predefinedCompanyId) return;

    if (isAddCompanySelected(predefinedCompanyId)) return;

    const initialCompany = companies.find(
      (company) => company.id === predefinedCompanyId,
    );

    if (!initialCompany) return;

    setCompanyDetails(initialCompany);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predefinedInitialValues?.company.companyId, companies]);

  useEffect(() => {
    setFilteredCompanies(
      companies.filter(
        (company) => company.contractorCompanyType === assignmentType,
      ),
    );
  }, [assignmentType, companies]);

  const loadContractorCompanyFile = async (companyId?: string) => {
    if (!companyId || !isSCWorkerType) return;

    const result = await dispatch(
      getContractorCompanyInfoFile({
        companyId,
        isAdminRole: isAdmin as boolean,
      }),
    );

    if (getContractorCompanyInfoFile.fulfilled.match(result)) {
      setContractorCompanyFileKeyInfo(generateUploadFileList(result.payload));
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleResetCompanyInfo = () => {
    setContractorCompanyFileKeyInfo([]);
    setCompanyDetails(undefined);
  };

  const handleCompanyChange = async (
    companyId: string,
    formik: FormikProps<WorkerInfoFormValues>,
  ) => {
    const selectedCompany = companies.find(({ id }) => id === companyId);
    const isAddCompany = isAddCompanySelected(companyId);

    if (isAddCompany) {
      handleResetCompanyInfo();
    } else {
      if (!selectedCompany?.representativeContact?.workerFileKey) {
        setContractorCompanyFileKeyInfo([]);
      } else {
        await loadContractorCompanyFile(selectedCompany?.id);
      }

      setCompanyDetails(selectedCompany);
    }

    formik.setFieldTouched('company', false, false);
    formik.setFieldValue(
      'company',
      {
        companyId,
        companyName: !isAddCompany ? selectedCompany?.companyName : '',
        firstName: !isAddCompany
          ? selectedCompany?.representativeContact?.firstName
          : '',
        lastName: !isAddCompany
          ? selectedCompany?.representativeContact?.lastName
          : '',
        email: !isAddCompany
          ? selectedCompany?.representativeContact?.email
          : '',
        phone: !isAddCompany
          ? selectedCompany?.representativeContact?.phoneNumber
          : '',
        country: !isAddCompany
          ? selectedCompany?.location?.countryId || countryUSAid
          : countryUSAid,
        internationalStateName: !isAddCompany
          ? selectedCompany?.location?.internationalStateName
          : undefined,
        state: !isAddCompany ? selectedCompany?.location?.stateId : undefined,
        internationalCityName: !isAddCompany
          ? selectedCompany?.location?.internationalCityName
          : undefined,
        city: !isAddCompany ? selectedCompany?.location?.city : '',
        address: !isAddCompany ? selectedCompany?.location?.address : '',
        zip:
          !isNil(selectedCompany?.location?.zipCode) && !isAddCompany
            ? selectedCompany?.location?.zipCode
            : '',
      },
      false,
    );

    if (!isSCWorkerType) return;

    formik.setFieldValue(
      'taxId',
      !isAddCompany ? selectedCompany?.representativeContact?.taxId : null,
      false,
    );
    formik.setFieldValue(
      'workerFileKey',
      !isAddCompany && selectedCompany?.representativeContact?.workerFileKey
        ? [selectedCompany?.representativeContact?.workerFileKey]
        : [],
      false,
    );
  };

  const isUploaderDisabled = () => {
    return (
      !formik.values.company.companyId ||
      isRenewOrEditRenewMode ||
      isRehireOrEditRehireMode ||
      !!companyDetails?.representativeContact?.workerFileKey ||
      hasActiveAssignmentRestriction
    );
  };

  return (
    <>
      {(isRenewOrEditRenewMode ||
        isRehireOrEditRehireMode ||
        hasActiveAssignmentRestriction) && (
        <div className={styles.hint}>
          <i className="icon icon-info-filled" />
          Contractor company/Parent Vendor info cannot be modified
        </div>
      )}
      <div className="modal-title">
        {isSCAssignment
          ? 'Parent subcontractor information'
          : 'Contractor company information'}
      </div>

      <Row gutter={40}>
        <Col xs={24} md={12} ref={companyFieldRef}>
          <FormItem
            name="company.companyId"
            label={
              <>
                <span>
                  {isSCAssignment ? 'Parent SC company *' : 'Company *'}
                </span>
                <Tooltip
                  overlayClassName={cx('light-tooltip', styles.modalTooltip)}
                  title={`This option lets you Create a new ${
                    isSCAssignment ? 'parent SC company' : 'contractor company'
                  } or Select an existing one.`}
                  trigger="click"
                  placement="topLeft"
                  getPopupContainer={() => companyFieldRef.current}
                  className={styles.modalTooltipOpener}
                >
                  <span className={cx('icon icon-attention-filled')} />
                </Tooltip>
              </>
            }
          >
            <FormikSelectWithLoading
              isLoading={!isCompaniesLoaded}
              showSearch
              name="company.companyId"
              placeholder={
                isSCAssignment ? 'Select parent SC company' : 'Select company'
              }
              onChange={(value: string) => handleCompanyChange(value, formik)}
              optionFilterProp="label"
              disabled={
                isRenewOrEditRenewMode ||
                isRehireOrEditRehireMode ||
                hasActiveAssignmentRestriction
              }
            >
              {isCompaniesLoaded && (
                <>
                  <FormikSelectWithLoading.Option
                    key="select-other-company"
                    value={ExtraItemEnum.AddCompany}
                  >
                    {getAddCompanyLabel(assignmentType)}
                  </FormikSelectWithLoading.Option>

                  {filteredCompanies.map((company) => (
                    <FormikSelectWithLoading.Option
                      key={`select-company-${company.id}`}
                      value={company.id || ''}
                      label={company.companyName}
                    >
                      {`${company.companyName}: ${company.representativeContact?.email}`}
                    </FormikSelectWithLoading.Option>
                  ))}
                </>
              )}
            </FormikSelectWithLoading>
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem
            name="company.companyName"
            label={
              isSCAssignment
                ? 'Parent SC company name *'
                : 'Contractor company name *'
            }
          >
            <Input
              name="company.companyName"
              placeholder={`Enter ${
                isSCAssignment ? 'parent SC company' : 'contractor company'
              } name`}
              disabled={!isAddCompanyOptionSelected}
              onChange={() => clearFormError(formik, 'company._formError')}
              autoComplete="off"
            />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem
            name="company.firstName"
            label="Primary contact first name *"
          >
            <Input
              name="company.firstName"
              disabled={!isAddCompanyOptionSelected}
              placeholder="Enter primary contact first name"
              autoComplete="off"
            />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem name="company.lastName" label="Primary contact last name *">
            <Input
              name="company.lastName"
              disabled={!isAddCompanyOptionSelected}
              placeholder="Enter primary contact last name"
              autoComplete="off"
            />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem name="company.email" label="Email *">
            <Input
              name="company.email"
              placeholder="Enter email"
              disabled={!isAddCompanyOptionSelected}
              onChange={() => clearFormError(formik, 'company._formError')}
              autoComplete="off"
            />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem label="Phone number *" name="company.phone">
            <Input
              name="company.phone"
              placeholder="Enter phone number"
              disabled={!isAddCompanyOptionSelected}
              autoComplete="off"
            />
          </FormItem>
        </Col>
      </Row>

      {formik.values.worker.workerType === WorkerTypeEnum.SC && (
        <>
          <Row gutter={40}>
            <Col xs={24} md={12}>
              <FormItem data-id="companyTaxIdField" label="Tax ID" name="taxId">
                <InputNumber
                  name="taxId"
                  disabled={
                    isRehireOrEditRehireMode ||
                    isRenewOrEditRenewMode ||
                    !(
                      formik.values.company.companyId &&
                      isNil(companyDetails?.representativeContact?.taxId)
                    ) ||
                    hasActiveAssignmentRestriction
                  }
                  placeholder="Enter tax ID"
                />
              </FormItem>
            </Col>
          </Row>

          <Row gutter={40}>
            <Col xs={24} md={12}>
              <h5 className="modal-title">
                Please use this section to upload worker's W9 Form
              </h5>
              <div className="upload-wrapper">
                <FormItem name="workerFileKey" showValidateError={false}>
                  <WorkerUploader
                    workerType={formik.values.worker.workerType}
                    fieldName="workerFileKey"
                    isAdmin={isAdmin as boolean}
                    workerFiles={contractorCompanyFileKeyInfo}
                    disabled={isUploaderDisabled()}
                    onChange={setContractorCompanyFileKeyInfo}
                  />
                </FormItem>
              </div>
            </Col>
          </Row>
        </>
      )}

      <div className="modal-title">Business address</div>

      {checkIsCountriesWithStatesDropdown(formik.values.company.country) ? (
        <CompanyStateForm
          formik={formik}
          isAddCompanyOptionSelected={isAddCompanyOptionSelected}
        />
      ) : (
        <InternationalCompanyStateForm
          formik={formik}
          isAddCompanyOptionSelected={isAddCompanyOptionSelected}
        />
      )}
    </>
  );
}
